import React, { useEffect, useMemo, useState } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';
import Button from '@atlaskit/button';
import { Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	fireScreenAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { periscopeRefetchableQuery } from '@atlassian/jira-relay/src/__generated__/periscopeRefetchableQuery.graphql';
import type { suggestions_periscope_SuggestionsPanel_fragmentRef$key } from '@atlassian/jira-relay/src/__generated__/suggestions_periscope_SuggestionsPanel_fragmentRef.graphql';
import type { suggestions_periscope_WorkSuggestionsAutoDevJobSwitcher_node$key } from '@atlassian/jira-relay/src/__generated__/suggestions_periscope_WorkSuggestionsAutoDevJobSwitcher_node.graphql';
import type { suggestions_periscope_WorkSuggestionsCompassSuggestionsSwitcher_node$key } from '@atlassian/jira-relay/src/__generated__/suggestions_periscope_WorkSuggestionsCompassSuggestionsSwitcher_node.graphql';
import type { suggestions_periscope_WorkSuggestionsPullRequestsSuggestionsSwitcher_node$key } from '@atlassian/jira-relay/src/__generated__/suggestions_periscope_WorkSuggestionsPullRequestsSuggestionsSwitcher_node.graphql';
import type { suggestions_periscope_WorkSuggestionsSwitcher_node$key } from '@atlassian/jira-relay/src/__generated__/suggestions_periscope_WorkSuggestionsSwitcher_node.graphql';
import type { uiPeriscopeQuery$data } from '@atlassian/jira-relay/src/__generated__/uiPeriscopeQuery.graphql';
import { PERISCOPE_ANALYTICS, WORK_SUGGESTIONS_TASKS_PAGE_SIZE } from '../../common/constants';
import type { OrderScore } from '../../common/types';
import { PeriscopeEmptyState } from '../../common/ui/empty-state';
import { getUniqueArray, sortBasedOnOrder, sortOnScoreOrder } from '../../common/utils';
import { AutoDevJobsPlanningSuccess } from './autodev-job';
import { BlockingIssueTask } from './blocking-issue';
import { CompassAnnouncementTask } from './compass-suggestions';
import { SecurityVulnerabilityTask } from './critical-vulnerability';
import { FailedBuildTask } from './failed-build';
import { FailedDeploymentTask } from './failed-deployment';
import { TaskTypeDropdownFilter } from './filter';
import { Header } from './header';
import { messages } from './messages';
import { PullRequestNeedsWorkTask } from './pull-request-needs-work';
import { PullRequestReviewTask } from './pull-request-review';
import { PullRequestCommentTask } from './pull-request-suggestions';

type SuggestionsTypeOption = {
	value: string;
	label: string;
};

const getSortScoreOrder = (scoreOrder: OrderScore) => {
	if (scoreOrder == null || scoreOrder.byTaskType == null) {
		return null;
	}
	return {
		major: scoreOrder.byTaskType.major || 0,
		minor: scoreOrder.byTaskType.minor || 0,
	};
};

export const SuggestionsPanel = ({ fragmentRef }: { fragmentRef: uiPeriscopeQuery$data }) => {
	const { formatMessage } = useIntl();
	const {
		data: { workSuggestions },
		hasNext,
		loadNext,
		isLoadingNext,
	} = usePaginationFragment<
		periscopeRefetchableQuery,
		suggestions_periscope_SuggestionsPanel_fragmentRef$key
	>(
		graphql`
			fragment suggestions_periscope_SuggestionsPanel_fragmentRef on Query
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				projectAris: { type: "[ID!]!" }
				first: { type: "Int" }
				after: { type: "String" }
				sprintAutoDiscoveryLimit: { type: "Int" }
				isAutoDevJobsSuggestionEnabled: { type: "Boolean!" }
				isPullRequestSuggestionsEnabled: { type: "Boolean!" }
				isCompassSuggestionsEnabled: { type: "Boolean!" }
			)
			@refetchable(queryName: "periscopeRefetchableQuery") {
				workSuggestions @required(action: THROW) @optIn(to: "WorkSuggestions") {
					suggestionsByProjects(
						cloudId: $cloudId
						projectAris: $projectAris
						sprintAutoDiscoveryLimit: $sprintAutoDiscoveryLimit
					) @optIn(to: "WorkSuggestionsByProjects") {
						commonSuggestions(first: $first, after: $after)
							@connection(key: "periscope__commonSuggestions") {
							edges {
								node {
									id
									__typename
									orderScore {
										byTaskType {
											major
											minor
										}
									}
									... on WorkSuggestionsCommon {
										...suggestions_periscope_WorkSuggestionsSwitcher_node
									}
								}
							}
						}
						autoDevJobsSuggestions
							@optIn(to: "WorkSuggestionsAutoDevJobs")
							@include(if: $isAutoDevJobsSuggestionEnabled) {
							id
							__typename
							orderScore {
								byTaskType {
									major
									minor
								}
							}
							... on WorkSuggestionsAutoDevJobTask {
								...suggestions_periscope_WorkSuggestionsAutoDevJobSwitcher_node
							}
						}
						pullRequestSuggestions
							@optIn(to: "WorkSuggestionsPeriscopePrSuggestions")
							@include(if: $isPullRequestSuggestionsEnabled) {
							id
							__typename
							orderScore {
								byTaskType {
									major
									minor
								}
							}
							... on WorkSuggestionsPeriscopeTask {
								...suggestions_periscope_WorkSuggestionsPullRequestsSuggestionsSwitcher_node
							}
						}
						compassSuggestions
							@optIn(to: "WorkSuggestionsCompassTask")
							@include(if: $isCompassSuggestionsEnabled) {
							id
							__typename
							orderScore {
								byTaskType {
									major
									minor
								}
							}
							... on WorkSuggestionsCompassTask {
								...suggestions_periscope_WorkSuggestionsCompassSuggestionsSwitcher_node
							}
						}
						sortOrder {
							defaultOrder
						}
					}
				}
			}
		`,
		fragmentRef,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);

	const commonSuggestions = useMemo(
		() =>
			workSuggestions?.suggestionsByProjects?.commonSuggestions?.edges
				?.map((edge) => edge?.node)
				.filter(Boolean),
		[workSuggestions],
	);
	const autoDevJobsSuggestions = useMemo(
		() => workSuggestions?.suggestionsByProjects?.autoDevJobsSuggestions,
		[workSuggestions],
	);

	const pullRequestSuggestions = useMemo(
		() => workSuggestions?.suggestionsByProjects?.pullRequestSuggestions,
		[workSuggestions],
	);

	const compassSuggestions = useMemo(
		() => workSuggestions?.suggestionsByProjects?.compassSuggestions,
		[workSuggestions],
	);

	const defaultOrder = workSuggestions?.suggestionsByProjects?.sortOrder?.defaultOrder;

	const combinedSuggestions = useMemo(
		() => [
			...(commonSuggestions
				? commonSuggestions.map((commonSuggestion) => ({
						id: commonSuggestion.id,
						__typename: commonSuggestion.__typename,
						orderScore: getSortScoreOrder(commonSuggestion.orderScore),
						node: <WorkSuggestionsSwitcher key={commonSuggestion.id} node={commonSuggestion} />,
					}))
				: []),
			...(autoDevJobsSuggestions
				? autoDevJobsSuggestions.map((autoDevJobsSuggestion) => ({
						id: autoDevJobsSuggestion.id,
						__typename: autoDevJobsSuggestion.__typename,
						orderScore: getSortScoreOrder(autoDevJobsSuggestion.orderScore),
						node: (
							<WorkSuggestionsAutoDevJobSwitcher
								key={autoDevJobsSuggestion.id}
								node={autoDevJobsSuggestion}
							/>
						),
					}))
				: []),

			...(pullRequestSuggestions
				? pullRequestSuggestions.map((prSuggestion) => ({
						id: prSuggestion.id,
						__typename: prSuggestion.__typename,
						orderScore: getSortScoreOrder(prSuggestion.orderScore),
						node: (
							<WorkSuggestionsPullRequestsSuggestionsSwitcher
								key={prSuggestion.id}
								node={prSuggestion}
							/>
						),
					}))
				: []),

			...(compassSuggestions
				? compassSuggestions.map((compassSuggestion) => ({
						id: compassSuggestion.id,
						__typename: compassSuggestion.__typename,
						orderScore: getSortScoreOrder(compassSuggestion.orderScore),
						node: (
							<WorkSuggestionsCompassSuggestionsSwitcher
								key={compassSuggestion.id}
								node={compassSuggestion}
							/>
						),
					}))
				: []),
		],
		[autoDevJobsSuggestions, commonSuggestions, pullRequestSuggestions, compassSuggestions],
	);

	const sortedSuggestions = useMemo(() => {
		if (combinedSuggestions == null) {
			return null;
		}
		if (fg('jira-work-suggestions-page-score-sorting')) {
			return sortOnScoreOrder(combinedSuggestions);
		}
		return defaultOrder ? sortBasedOnOrder(combinedSuggestions, defaultOrder) : null;
	}, [combinedSuggestions, defaultOrder]);

	const taskTypeOptions = useMemo(() => {
		if (!sortedSuggestions || sortedSuggestions.length === 0) {
			return [];
		}
		const uniqueSuggestionsType = getUniqueArray(
			sortedSuggestions?.map((suggestion) => suggestion.__typename),
		);
		return uniqueSuggestionsType
			.map((type) => {
				const dropdownLabelMessage = getDropdownFilterMessageLabel(type);
				return dropdownLabelMessage
					? {
							label: formatMessage(dropdownLabelMessage),
							value: type,
						}
					: null;
			})
			.filter((option): option is SuggestionsTypeOption => option != null);
	}, [formatMessage, sortedSuggestions]);

	// Get the selected task types from available task types
	const finalSelectedTaskType = useMemo(
		() =>
			selectedTaskTypes.filter((type) => taskTypeOptions.some((option) => option.value === type)),
		[selectedTaskTypes, taskTypeOptions],
	);

	const finalSuggestions = useMemo(
		() =>
			sortedSuggestions?.filter((suggestion) => {
				if (finalSelectedTaskType.length > 0) {
					return finalSelectedTaskType.includes(suggestion.__typename);
				}
				return true;
			}),
		[finalSelectedTaskType, sortedSuggestions],
	);
	useEffect(() => {
		if (combinedSuggestions != null) {
			const analyticsEvent = createAnalyticsEvent({
				action: 'viewed',
			});

			fireScreenAnalytics(analyticsEvent, { suggestionsCount: combinedSuggestions.length });
		}
	}, [combinedSuggestions, createAnalyticsEvent]);

	if (finalSuggestions == null) {
		return null;
	}

	if (combinedSuggestions.length === 0) {
		return (
			<Flex direction="column" gap="space.300" alignItems="start" xcss={workSuggestionsPanelStyles}>
				<Header />
				<PeriscopeEmptyState />
			</Flex>
		);
	}

	return (
		<Flex direction="column" alignItems="start" xcss={workSuggestionsPanelStyles}>
			<Flex
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				xcss={headerWrapperStyles}
			>
				<Header />

				<Flex justifyContent="end">
					<TaskTypeDropdownFilter
						options={taskTypeOptions}
						selectedTypes={finalSelectedTaskType}
						onSelect={(types) => {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'updated',
									actionSubject: 'button',
								}),
								`${PERISCOPE_ANALYTICS}.workSuggestions.suggestionTypeList`,
								{
									filteredTypes: types,
									filteredSuggestionsCount: combinedSuggestions.filter(({ __typename }) =>
										types.includes(__typename),
									).length,
								},
							);
							setSelectedTaskTypes(types);
						}}
					/>
				</Flex>
			</Flex>
			<Flex direction="column" alignItems="start" xcss={workSuggestionsWrapperStyles}>
				<Flex direction="column" gap="space.400" xcss={WorkSuggestionsContainerStyles}>
					{finalSuggestions.map((suggestion) => suggestion.node)}
				</Flex>
				{hasNext && !isLoadingNext && (
					<Flex justifyContent="center" alignItems="center" xcss={LoadingNextContainerStyles}>
						<Button onClick={() => loadNext(WORK_SUGGESTIONS_TASKS_PAGE_SIZE)}>
							{formatMessage(messages.loadingTextNonFinal)}
						</Button>
					</Flex>
				)}
				{isLoadingNext && (
					<Flex justifyContent="center" alignItems="center" xcss={LoadingNextContainerStyles}>
						<Spinner size="large" label={formatMessage(messages.loadingTextNonFinal)} />
					</Flex>
				)}
			</Flex>
		</Flex>
	);
};

export const getDropdownFilterMessageLabel = (type: string) => {
	switch (type) {
		case 'WorkSuggestionsBuildTask':
			return messages.failedBuildSuggestionsLabelNonFinal;
		case 'WorkSuggestionsDeploymentTask':
			return messages.failedDeploymentSuggestionsLabelNonFinal;
		case 'WorkSuggestionsPullRequestReviewTask':
			return messages.pullRequestReviewSuggestionsLabelNonFinal;
		case 'WorkSuggestionsPullRequestNeedsWorkTask':
			return messages.pullRequestNeedsToWorkSuggestionsLabelNonFinal;
		case 'WorkSuggestionsCriticalVulnerabilityTask':
			return messages.securityVulnerabilitySuggestionsLabelNonFinal;
		case 'WorkSuggestionsBlockingIssueTask':
			return messages.blockingIssuesSuggestionsLabelNonFinal;
		case 'WorkSuggestionsAutoDevJobsPlanSuccessTask':
			return messages.autodevJobPlanningSuccessSuggestionsLabelNonFinal;
		case 'WorkSuggestionsPRCommentsTask':
			return messages.pullRequestCommentsSuggestionsLabelNonFinal;
		case 'WorkSuggestionsCompassAnnouncementTask':
			return messages.compassAnnouncementSuggestionsLabelNonFinal;
		default:
			return null;
	}
};

export const WorkSuggestionsAutoDevJobSwitcher = ({
	node,
}: {
	node: suggestions_periscope_WorkSuggestionsAutoDevJobSwitcher_node$key;
}) => {
	const data = useFragment(
		graphql`
			fragment suggestions_periscope_WorkSuggestionsAutoDevJobSwitcher_node on WorkSuggestionsAutoDevJobTask {
				__typename
				... on WorkSuggestionsAutoDevJobsPlanSuccessTask {
					...planningSuccess_periscope_AutoDevJobsPlanningSuccess
				}
			}
		`,
		node,
	);

	switch (data.__typename) {
		case 'WorkSuggestionsAutoDevJobsPlanSuccessTask':
			return <AutoDevJobsPlanningSuccess fragmentRef={data} />;
		default:
			return null;
	}
};

export const WorkSuggestionsPullRequestsSuggestionsSwitcher = ({
	node,
}: {
	node: suggestions_periscope_WorkSuggestionsPullRequestsSuggestionsSwitcher_node$key;
}) => {
	const data = useFragment(
		graphql`
			fragment suggestions_periscope_WorkSuggestionsPullRequestsSuggestionsSwitcher_node on WorkSuggestionsPeriscopeTask {
				__typename
				... on WorkSuggestionsPRCommentsTask {
					...pullRequestCommentSuggestions_periscope_PullRequestCommentTask
				}
			}
		`,
		node,
	);

	switch (data.__typename) {
		case 'WorkSuggestionsPRCommentsTask':
			return <PullRequestCommentTask fragmentRef={data} />;
		default:
			return null;
	}
};

export const WorkSuggestionsCompassSuggestionsSwitcher = ({
	node,
}: {
	node: suggestions_periscope_WorkSuggestionsCompassSuggestionsSwitcher_node$key;
}) => {
	const data = useFragment(
		graphql`
			fragment suggestions_periscope_WorkSuggestionsCompassSuggestionsSwitcher_node on WorkSuggestionsCompassTask {
				__typename
				... on WorkSuggestionsCompassAnnouncementTask {
					...announcement_periscope_CompassAnnouncementTask
				}
			}
		`,
		node,
	);

	switch (data.__typename) {
		case 'WorkSuggestionsCompassAnnouncementTask':
			return <CompassAnnouncementTask fragmentRef={data} />;
		default:
			return null;
	}
};

export const WorkSuggestionsSwitcher = ({
	node,
}: {
	node: suggestions_periscope_WorkSuggestionsSwitcher_node$key;
}) => {
	const data = useFragment(
		graphql`
			fragment suggestions_periscope_WorkSuggestionsSwitcher_node on WorkSuggestionsCommon {
				__typename
				id
				... on WorkSuggestionsBuildTask {
					...failedBuild_periscope_FailedBuildTask
				}
				... on WorkSuggestionsDeploymentTask {
					...failedDeployment_periscope_FailedDeploymentTask
				}
				... on WorkSuggestionsPullRequestReviewTask {
					...pullRequestReview_periscope_PullRequestReviewTask
				}
				... on WorkSuggestionsPullRequestNeedsWorkTask {
					...pullRequestNeedsWork_periscope_PullRequestNeedsWorkTask
				}
				... on WorkSuggestionsCriticalVulnerabilityTask {
					...criticalVulnerability_periscope_SecurityVulnerabilityTask
				}
				... on WorkSuggestionsBlockingIssueTask {
					...blockingIssue_periscope_BlockingIssueTask
				}
			}
		`,
		node,
	);

	switch (data.__typename) {
		case 'WorkSuggestionsBuildTask':
			return <FailedBuildTask fragmentRef={data} />;
		case 'WorkSuggestionsDeploymentTask':
			return <FailedDeploymentTask fragmentRef={data} />;
		case 'WorkSuggestionsPullRequestReviewTask':
			return <PullRequestReviewTask fragmentRef={data} />;
		case 'WorkSuggestionsPullRequestNeedsWorkTask':
			return <PullRequestNeedsWorkTask fragmentRef={data} />;
		case 'WorkSuggestionsCriticalVulnerabilityTask':
			return <SecurityVulnerabilityTask fragmentRef={data} />;
		case 'WorkSuggestionsBlockingIssueTask':
			return <BlockingIssueTask fragmentRef={data} />;
		default:
			return null;
	}
};

const workSuggestionsPanelStyles = xcss({
	width: '100%',
	paddingLeft: 'space.500',
	paddingBottom: 'space.400',
	paddingRight: 'space.500',
});

const workSuggestionsWrapperStyles = xcss({
	width: '100%',
});

const WorkSuggestionsContainerStyles = xcss({
	width: '100%',
	minWidth: '400px',
});

const LoadingNextContainerStyles = xcss({
	width: '100%',
	marginTop: 'space.100',
});

const headerWrapperStyles = xcss({
	width: '100%',
	marginBottom: 'space.200',
});
